import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import Layout from "../../components/Layout";
import AudienceEditor, { DEFAULT_AUDIENCE } from "../../components/AudienceEditor";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Hash, Plus, Edit, Trash2, X, Target } from "lucide-react";

/**
 * AdminForum – Administrer forum-rom (opprette, redigere, sette målgruppe).
 * Owner/admin only.
 */
export default function AdminForum() {
  const { user: me } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {forum, isNew, categoryId}

  const load = () => {
    setLoading(true);
    api.get("/forum").then((r) => setData(r.data || []))
      .catch(() => toast.error("Kunne ikke laste forum"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const isAdmin = me?.role === "owner" || me?.role === "admin";

  if (!isAdmin) {
    return (
      <Layout>
        <div className="text-center text-[var(--cream-soft)] py-20">
          Du må være admin for å se denne siden.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="admin-forum-page">
        <div className="mb-8">
          <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-1 flex items-center gap-2">
            <Hash className="h-3 w-3" /> ADMIN · FORUM
          </div>
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Forum-rom</h1>
          <p className="text-sm text-[var(--cream-soft)] mt-2 max-w-2xl">
            Opprett nye rom under hver kategori og sett målgruppe-filter for å
            begrense hvem som ser eller kan delta.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-[var(--cream-soft)] py-12">Laster…</div>
        ) : (
          <div className="space-y-8">
            {data.map(({ category, forums }) => (
              <section key={category.id} className="panel p-5" data-testid={`cat-${category.slug}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">
                      {category.name}
                    </h2>
                    <p className="text-xs text-[var(--cream-soft)] mt-1">{category.description}</p>
                  </div>
                  <button
                    onClick={() => setEditing({ forum: { audience: { ...DEFAULT_AUDIENCE } }, isNew: true, categoryId: category.id })}
                    className="px-3 py-2 text-xs font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90"
                    data-testid={`add-forum-${category.slug}`}
                  >
                    <Plus className="h-3 w-3 inline mr-1" /> NYTT ROM
                  </button>
                </div>
                {forums.length === 0 ? (
                  <div className="text-xs text-[var(--cream-faint)] italic">Ingen rom enda i denne kategorien.</div>
                ) : (
                  <div className="space-y-2">
                    {forums.map((f) => (
                      <ForumRow key={f.id} forum={f} onEdit={() => setEditing({ forum: f, isNew: false, categoryId: category.id })} onReload={load} />
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}

        {editing && (
          <EditDialog
            forum={editing.forum}
            isNew={editing.isNew}
            categoryId={editing.categoryId}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </div>
    </Layout>
  );
}

function ForumRow({ forum, onEdit, onReload }) {
  const hasAudience = forum.audience?.enforce_visibility || forum.audience?.enforce_participation;
  const remove = async () => {
    if (!confirm(`Slette rom «${forum.name}»?`)) return;
    try {
      await api.delete(`/admin/forums/${forum.id}`);
      toast.success("Slettet");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke slette");
    }
  };
  return (
    <div className="flex items-center justify-between border border-white/5 px-4 py-3" data-testid={`forum-row-${forum.slug}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: forum.color }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[var(--cream)] font-meta flex items-center gap-2">
            {forum.name}
            {hasAudience && (
              <span className="px-1.5 py-0.5 text-[9px] font-meta tracking-[0.15em] bg-[var(--magenta)]/20 text-[var(--magenta)] flex items-center gap-1">
                <Target className="h-2.5 w-2.5" /> MÅLGRUPPE
              </span>
            )}
          </div>
          <div className="text-[11px] text-[var(--cream-faint)] mt-0.5">
            {forum.thread_count || 0} tråder · {forum.post_count || 0} innlegg
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onEdit} className="p-2 text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid={`edit-forum-${forum.slug}`}>
          <Edit className="h-3.5 w-3.5" />
        </button>
        {(forum.thread_count || 0) === 0 && (
          <button onClick={remove} className="p-2 text-[var(--cream-soft)] hover:text-red-400" data-testid={`del-forum-${forum.slug}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function EditDialog({ forum, isNew, categoryId, onClose, onSaved }) {
  const [name, setName] = useState(forum.name || "");
  const [description, setDescription] = useState(forum.description || "");
  const [color, setColor] = useState(forum.color || "");
  const [audience, setAudience] = useState(forum.audience || { ...DEFAULT_AUDIENCE });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/admin/forums", { category_id: categoryId, name, description, color: color || null, audience });
        toast.success("Rom opprettet");
      } else {
        await api.patch(`/admin/forums/${forum.id}`, { name, description, color: color || null, audience });
        toast.success("Lagret");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="forum-edit-dialog">
      <div className="bg-[var(--noir-elev)] border border-white/10 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex justify-between items-start">
          <div>
            <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-1">
              {isNew ? "NYTT FORUM-ROM" : "REDIGER ROM"}
            </div>
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{name || "(uten navn)"}</h2>
          </div>
          <button onClick={onClose} className="text-[var(--cream-soft)] hover:text-[var(--cream)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">NAVN</div>
            <input value={name} onChange={(e) => setName(e.target.value)} className="input-editorial w-full" placeholder="Eks. «For kvinner over 30»" data-testid="forum-name-input" />
          </div>
          <div>
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">BESKRIVELSE</div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input-editorial w-full" placeholder="Kort om rommet…" data-testid="forum-desc-input" />
          </div>
          <div>
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">FARGE (HEX, VALGFRI)</div>
            <input value={color} onChange={(e) => setColor(e.target.value)} className="input-editorial w-full" placeholder="#F71868" data-testid="forum-color-input" />
          </div>

          <AudienceEditor value={audience} onChange={setAudience} />
        </div>
        <div className="p-6 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--cream)]">AVBRYT</button>
          <button onClick={save} disabled={busy || !name.trim()} className="px-4 py-2 text-xs font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40" data-testid="forum-save-btn">
            {busy ? "..." : "LAGRE"}
          </button>
        </div>
      </div>
    </div>
  );
}
